import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Image, FlatList, Linking } from 'react-native';
import type { OrderProps, PlayerFollowerProps, PlayerFollowerStatsProps, PlayerLinkProps, PublicPlayerProps } from '../../types';
import { SocialProfileApi } from './api';
import PlayerCard from '../PlayerCard';
import PlayerFollowersList from './components/PlayerFollowersList';
import PostsList from './components/PostsList';
import SocialOrdersList from '../SocialOrdersList';
import PlayerFollowButton from '../PlayerFollowButton';
import { Button, Text, View } from '../../Components/Themed';
import { Icons, Toggle } from '../../Components';
import { useColors } from '../../constants/useColors';

type PlayerProfileProps = {
    viewing_player_id:string,
    player_id?:string,
    height?:number,
    padding_insets?:{ top:number, bottom:number },
    onStartChat?:(player_id:string) => void,
    onHeadToHead?: (player_id:string) => void,
    onFadeOrder: (order:OrderProps) => void,
    onCopyOrder:(order:OrderProps) => void,
    onShareProfile?:(player_id:string) => void,
    onSharePost?:(post_id:string) => void,
    onViewPostComments:(post_id:string) => void,
    onSelectPlayer:(player:PublicPlayerProps) => void,
    onRequestAuthenticate:() => void,
    onEditProfile:() => void,
    onBack?:() => void
}

const profile_sections = ['gap','info','engage','followers','following','activity_toggle', 'orders','posts']
const PlayerProfile = ({ viewing_player_id, padding_insets, player_id, height, onStartChat, onHeadToHead, onCopyOrder, onFadeOrder, onViewPostComments, onEditProfile, onSelectPlayer, onRequestAuthenticate, onBack }:PlayerProfileProps) => {
    const Colors = useColors();
    const [ module_size, setModuleSize ] = useState({ height:755, width:330 });
    const [ show_following, setShowFollowing ] = useState<{
        visible:boolean,
        status: 'following'|'followers'
    }>({
        visible: false,
        status: 'following'
    })
    const [ my_data, setMyData ] = useState<{
        me_loading:boolean,
        player_follower?:PlayerFollowerProps
    }>({
        me_loading:false
    });
    const { player_follower } = my_data;
    
    const [ activity_data, setActivityData ] = useState<{
        activity_loading:boolean,
        orders:OrderProps[],
        activity_offset:number
    }>({
        activity_loading: false,
        orders:[],
        activity_offset: 0
    });
    const { orders } = activity_data;

    const [ follow_data, setFollowData ] = useState<{
        follow_loading:boolean,
        player_followers:PlayerFollowerProps[],
        players_following:PlayerFollowerProps[]
    }>({
        follow_loading: false,
        player_followers: [],
        players_following: []
    });
    const { follow_loading, player_followers, players_following } = follow_data;
    const top_3_followers = player_followers.slice(0, 4);
    const top_3_following = players_following.slice(0,4);
    const [ profile_data, setData ] = useState<{
        loading:boolean,
        player?:PublicPlayerProps,
        player_links:PlayerLinkProps[],
        active_toggle:string,
        player_follower_stats?:PlayerFollowerStatsProps
    }>({
        loading:false,
        player_links:[],
        active_toggle:'orders'
    });
    const { loading, player, active_toggle, player_links, player_follower_stats } = profile_data;

    useEffect(() => {
        SocialProfileApi.setEnvironment()
        getDataFromServer()
        getFollowDataFromServer();
    },[viewing_player_id]);

    useEffect(() => {
        if(!player_id){ return }
        getMyDataFromServer(player_id)
    },[player_id])

    useEffect(() => {
        switch(active_toggle){
            case 'orders':
                getOrderData(0);
                return
            default: return
        }
    },[active_toggle])

    const getOrderData = async(offset:number) => {
        setActivityData({ ...activity_data, activity_loading: true });
        const os = await SocialProfileApi.getOrdersByPlayerId(viewing_player_id, offset)
        setActivityData({
            ...activity_data,
            activity_loading: false,
            orders: os,
            activity_offset: offset
        })
    }

    const getDataFromServer = async() => {
        setData({ ...profile_data, loading:true });
        const p = await SocialProfileApi.getPlayerById(viewing_player_id)
        const pl = await SocialProfileApi.getPlayerLinksByPlayerId(viewing_player_id);
        const pfs = await SocialProfileApi.getFollowerStats(viewing_player_id);
        setData({
            ...profile_data,
            loading:false,
            player:p,
            player_links:pl,
            player_follower_stats: pfs
        })
    }

    const getFollowDataFromServer = async() => {
        setFollowData({ ...follow_data, follow_loading: true });
        const followers = await SocialProfileApi.getPlayerFollowersByPlayerId(viewing_player_id, 'followers', 0);
        const following = await SocialProfileApi.getPlayerFollowersByPlayerId(viewing_player_id, 'following', 0);

        setFollowData({
            ...follow_data,
            follow_loading: false,
            player_followers: followers,
            players_following: following
        })
    }

    const getMyDataFromServer = async(player_id:string) => {
        setMyData({ ...my_data, me_loading:true });
        const pf = await SocialProfileApi.getFollowerStatus(player_id, viewing_player_id);
        setMyData({
            ...my_data,
            me_loading: false,
            player_follower: pf
        })
    }


    const renderFollowers = (data: { item:PlayerFollowerProps, index:number }) => {
        if(!data.item.playerDetails){ return <></> }
        return (
            <View float style={{ margin:3 }}>
                <PlayerCard
                    player={data.item.playerDetails}
                    width={120}
                    alignment='vertical'
                    onSelectPlayer={(p) => onSelectPlayer(p)}
                    onFollowingUpdate={(pf) => console.log(pf)}
                />
            </View>
        )
    }


    const renderLinks = (data: { item:PlayerLinkProps, index:number }) => {
        return (
            <Button style={{ marginLeft:5, marginRight:5 }} onPress={() => Linking.openURL(data.item.link_full)}>
                {data.item.link_type == 'twitter' ?
                <Icons.TwitterIcon 
                    size={30}
                />
                :data.item.link_type == 'custom' ?
                <Icons.LinkIcon
                    color={Colors.text.h1}
                    size={30}
                />
                :data.item.link_type == 'linked_in' ?
                <Icons.LinkedInIcon
                    size={30}
                />
                :<></>}
            </Button>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'gap':
                return (
                    <View transparent style={{ height:300 }} />
                )
            case 'info':
                if(!player){ return <></> }
                return (
                    <View style={{ borderTopRightRadius: 22, borderTopLeftRadius:22 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20, borderTopRightRadius: 22, borderTopLeftRadius:22 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>{player.first_name} {player.last_name}</Text>
                                <Text style={{ marginTop:4 }} theme='description'>@{player.username}</Text>
                            </View>
                            {player_id && player_id != viewing_player_id ?
                                <PlayerFollowButton
                                player_id={player_id}
                                onFollowingUpdate={(pf) => {
                                    setMyData({
                                        ...my_data,
                                        player_follower: pf
                                    })
                                }}
                                following={player_follower}

                            />  
                            :player_id && player_id == viewing_player_id ?
                            <Button
                                title='Edit Profile'
                                type='action'
                                onPress={() => onEditProfile()}
                            />
                            :<></>}
                        </View>
                        <View style={{ padding:10 }}>
                            <Text theme='description'>{player.bio && player.bio != '' ? player.bio : `${player.username} has not set a bio.`}</Text>
                        </View>
                    </View>
                )
            case 'engage':
                if(!player){ return <></> }
                if(viewing_player_id == player_id){ return <></> }
                return (
                    <View style={{ paddingTop:20 }}>
                        <View type='header' style={{  padding:20 }}>
                            <Text theme='h1'>Engage With @{player.username}</Text>
                            <Text style={{ marginTop:3 }} theme='description'>Select an engagement below</Text>
                        </View>
                        <View style={{ marginTop:10, padding:5, paddingLeft:20, paddingRight:20, paddingTop:0 }}>
                            {onStartChat ?
                            <Button style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light  }} onPress={() => onStartChat(viewing_player_id)}>
                                <Text style={{ flex:1 }} theme='description'>Start a Chat</Text>
                                <View style={{ height:30, width:30, borderRadius:4, backgroundColor:Colors.text.action, justifyContent:'center', alignItems:'center' }}>
                                    <Icons.ChatIcon size={22} color={Colors.text.white} />
                                </View>
                            </Button>
                            :<></>}
                            {onHeadToHead ?
                            <Button style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light  }} onPress={() => onHeadToHead(viewing_player_id)}>
                                <Text style={{ flex:1 }} theme='description'>Challenge Head to Head</Text>
                                <View style={{ height:30, width:30, borderRadius:4, backgroundColor:Colors.text.error, justifyContent:'center', alignItems:'center' }}>
                                    <Icons.HeadToHeadIcon size={22} color={Colors.text.white} />
                                </View>
                            </Button>
                            :<></>}
                            {player_links.length > 0 ?
                            <View style={{ flexDirection:'row', alignItems:'center', paddingLeft:10, paddingRight:5 }}>
                                <View style={{ flex:1 }}>
                                    <Text theme='description'>Checkout Socials</Text>
                                </View>
                                <View style={{ alignSelf:'flex-end' }}>
                                    <FlatList
                                        data={player_links}
                                        renderItem={renderLinks}
                                        keyExtractor={(item) => item.player_link_id.toString()}
                                        horizontal
                                    />
                                </View>
                            </View>
                            :<></>}
                        </View>
                    </View>
                )
            case 'followers':
                if(!player){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                            <View transparent>
                                <Text theme='h1'>Followers</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Who is following @{player.username}</Text>
                            </View>
                            <View transparent style={{ padding:10, flex:1, alignItems:'flex-end' }}>
                                <Text theme='h1'>{player_follower_stats?.following_count ?? 0}</Text>
                            </View>
                            <Button
                                title='View All'
                                type='action'
                                onPress={() => setShowFollowing({ visible:true, status: 'followers' })}
                            />
                        </View>
                        <View type='body' style={{ padding:10 }}>
                            <View type='row'>
                                {follow_loading ?
                                <ActivityIndicator size={'large'} color={Colors.text.h1} />
                                :<></>}
                                <View style={{ flex:1 }}>
                                    <FlatList
                                        key='top_followers'
                                        horizontal
                                        showsHorizontalScrollIndicator={false}
                                        data={top_3_followers}
                                        renderItem={renderFollowers}
                                        keyExtractor={(item) => item.player_follower_id.toString()}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                )
            case 'following':
                if(!player){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                            <View transparent>
                                <Text theme='h1'>Following</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Who is @{player.username} following</Text>
                            </View>
                            <View transparent style={{ padding:10, flex:1, alignItems:'flex-end' }}>
                                <Text theme='h1'>{player_follower_stats?.follower_count ?? 0}</Text>
                            </View>
                            <Button
                                title='View All'
                                type='action'
                                onPress={() => setShowFollowing({ visible:true, status: 'following' })}
                                />
                        </View>
                        <View type='body' style={{ padding:10 }}>
                            <View type='row'>
                                {follow_loading ?
                                <ActivityIndicator size={'large'} color={Colors.text.h1} />
                                :<></>}
                                <View style={{ flex:1 }}>
                                    <FlatList
                                        key='top_following'
                                        showsHorizontalScrollIndicator={false}
                                        horizontal
                                        data={top_3_following}
                                        renderItem={renderFollowers}
                                        keyExtractor={(item) => item.player_follower_id.toString()}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                )
            case 'activity_toggle':
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ paddingLeft:10}}>
                                <Text theme='h1'>Player Activity</Text>
                                <Text style={{ marginTop:3 }} theme='description'>View recent player activity below</Text>
                            </View>
                            <View style={{ flex:1 }}>
                            <Toggle
                                options={[
                                    { key: 'posts', label: 'Posts' },
                                    { key: 'orders', label: 'Orders' }
                                ]}
                                selected_option={active_toggle}
                                onSelectOption={(option) => setData({ ...profile_data, active_toggle: option })}
                            />
                            </View>
                        </View>
                    </View>
                )
            case 'orders':
                if(!player || active_toggle != 'orders'){ return <></> }
                return (
                    <View style={{ padding:15 }}>
                        <SocialOrdersList
                            players={[player]}
                            orders={orders}
                            
                            direction='vertical'
                            onCopyOrder={onCopyOrder}
                            onFadeOrder={onFadeOrder}
                        />
                    </View>
                )
            case 'posts':
                if(!player || active_toggle != 'posts'){ return <></> }
                return (
                    <PostsList
                        viewing_player={player}
                        player_id={player_id}
                        onRequestAuthenticate={onRequestAuthenticate}
                        onCopyOrder={onCopyOrder}
                        onFadeOrder={onFadeOrder}
                        onViewComments={onViewPostComments}
                    />
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }} onLayout={(ev) => {
            const { height, width } = ev.nativeEvent.layout
            setModuleSize({ width, height });
        }}>
            <View float style={{ position:'absolute', top:0, left:0, right:0, borderBottomRightRadius:50, borderBottomLeftRadius:50 }} nativeID='profile_image'>
                <Image
                    source={{ uri: player?.profile_pic && player.profile_pic != '' ? player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                    style={{ width: module_size.width, height: module_size.width, borderBottomRightRadius:50, borderBottomLeftRadius:50 }}
                    resizeMode='cover'
                />
                
            </View>
            <FlatList
                data={profile_sections}
                key='profile_sections_list'
                refreshing={loading}
                onRefresh={() => getDataFromServer()}
                keyExtractor={(item) => item}
                renderItem={renderSections}
            />
            {onBack ?
            <Button 
                float
                style={{ position:'absolute', top:padding_insets?.top ?? 0, left:0, height:50, width:50, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                onPress={() => onBack()}>
                <Icons.ChevronIcon direction='left' color={Colors.text.h1} size={14} />
            </Button> 
            :<></>}
            {player && show_following.visible ?
            <View style={{ position:'absolute', top:0, left:0, right:0, bottom:0, paddingTop:padding_insets?.top, paddingBottom:padding_insets?.bottom }}>
                    <PlayerFollowersList
                        height={height}
                        viewing_player={player}
                        player_id={player_id}
                        onSelectPlayer={onSelectPlayer}
                        status={show_following.status}
                        total={show_following.status == 'followers' ? player_follower_stats?.follower_count : player_follower_stats?.following_count}
                        onClose={() => setShowFollowing({ ...show_following, visible:false })}
                    />
            </View>
            :<></>}
        </View>
    )
}

export default PlayerProfile
